import React from 'react';
import { v4 as uuid } from 'uuid';
import 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { useCreateFolder } from 'containers/Sequences/queries/useCreateFolder';
import { useCreateFicheDePrep } from 'queries/fdp/useCreateFichedePrep';
import { useCreateStandaloneFicheDePrep } from 'queries/fdp/useCreateStandaloneFicheDePrep';
import { Menu } from 'components/Menu';
import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  FolderIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { FicheDePrep } from 'types/sequences';
import { useTranslation } from 'react-i18next';
import emptyBo from 'components/Bo/emptyBo';
import { useCreateEmptySequence } from 'queries/sequences/useCreateSequence';
import { useHasAccessToFDPTemplates } from 'containers/Sequences/queries/useHasAccessToFDPTemplates';

interface Props {
  folderId?: string;
  ficheDePrepId?: string;
  sequenceId?: string;
  minimal?: boolean;
  outlined?: boolean;
  fill?: boolean;
  className?: string;
  ['data-testid']?: string;
}

export function emptyFicheDePrep(sequenceId?: string): FicheDePrep {
  return {
    id: uuid(),
    sequenceId: sequenceId || uuid(),
    title: '',
    bo: emptyBo,
    niveaux: [],
    materials: [],
    attachments: [],
    phases: [],
  };
}

export function CreateButton(props: Props) {
  const { folderId, sequenceId } = props;
  const navigate = useNavigate();
  const createFolder = useCreateFolder();
  const createFdp = useCreateFicheDePrep();
  const { mutateAsync: createStandaloneFicheDePrep } =
    useCreateStandaloneFicheDePrep();
  const createEmptySequence = useCreateEmptySequence();
  const onCreate = async () => {
    await createEmptySequence({ folderId });
  };
  const { t } = useTranslation(['sequence', 'common']);

  const hasAccessToFDPTemplates = useHasAccessToFDPTemplates();

  return (
    <Menu portal fill>
      <Menu.Button
        intent="primary"
        minimal={props.minimal}
        outlined={props.outlined}
        fill={props.fill}
        rightIcon={ChevronDownIcon}
        className={props.className}
        data-testid={props['data-testid']}
        disabled={!!props.ficheDePrepId}
      >
        {t('common:buttons.add')}
      </Menu.Button>
      <Menu.Items>
        {folderId && (
          <Menu.Item
            onClick={() => {
              const newFolderId = uuid();
              return createFolder({
                id: newFolderId,
                name: t('sequence:actions.newFolder'),
                parentId: folderId === 'root' ? undefined : folderId,
              }).then(() =>
                navigate(`/sequences/folders/${newFolderId}?new=true`),
              );
            }}
            icon={(p => <FolderIcon {...p} />) as any}
            text={t('sequence:actions.aFolder')}
          />
        )}
        {!sequenceId && (
          <Menu.Item
            onClick={onCreate}
            icon={(p => <RectangleStackIcon {...p} />) as any}
            text={t('sequence:actions.aSequence')}
          />
        )}
        {sequenceId && (
          <Menu.Item
            onClick={() =>
              createFdp({
                sequenceId,
                ficheDePrep: emptyFicheDePrep(sequenceId),
              }).then(newFdp =>
                navigate(
                  `/sequences/${sequenceId}/fiches-de-prep/${newFdp.id}`,
                ),
              )
            }
            icon={(p => <DocumentTextIcon {...p} />) as any}
            text={t('sequence:actions.aFicheDePrep')}
          />
        )}
        {!sequenceId && (
          <Menu.Item
            onClick={() =>
              createStandaloneFicheDePrep({
                ficheDePrep: emptyFicheDePrep(),
                folderId: folderId === 'root' ? undefined : folderId,
              }).then(newFdp => navigate(`/fiches-de-prep/${newFdp.id}`))
            }
            icon={(p => <DocumentTextIcon {...p} />) as any}
            text={t('sequence:actions.aStandaloneFicheDePrep')}
          />
        )}
        {hasAccessToFDPTemplates && (
          <Menu.Item
            onClick={() => navigate('/fiches-de-prep/new/with-template')}
            icon={(p => <ClipboardDocumentIcon {...p} />) as any}
            text={t('sequence:actions.aTemplate')}
          />
        )}
      </Menu.Items>
    </Menu>
  );
}
